import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { C } from "@/brand/tokens";
import { SiteHeader6 } from "@/components/layout/SiteHeader6";
import { SiteFooter6 } from "@/components/layout/SiteFooter6";
import { Breadcrumb } from "@/components/common/Breadcrumb";
import { Box6 } from "@/components/common/Box6";
import { Eyebrow6 } from "@/components/common/Eyebrow6";
import { Cta6 } from "@/components/common/Cta6";
import { TextLink6 } from "@/components/common/TextLink6";
import { CtaBand6 } from "@/components/common/CtaBand6";
import { Pill6 } from "@/components/common/Pill6";
import { FocusAreas6 } from "@/components/sections6/FocusAreas6";
import { pillars6, pillarSlugToIndex6, brand6 } from "@/brand/contentF6";
import { fetchResources } from "@/lib/resourcesApi";
import { usePageMeta } from "@/lib/usePageMeta";

function BoxLabel({ children }) {
  return (
    <p
      className="font-inter text-[11px] uppercase font-semibold pb-4 mb-6"
      style={{ color: C.accentMetal, letterSpacing: "0.26em", borderBottom: `1px solid ${C.brassSoft}` }}
    >
      {children}
    </p>
  );
}

/** ServicePageF6 — detail page for one of the four service pillars. */
export default function ServicePageF6() {
  const { slug } = useParams();
  const idx = pillarSlugToIndex6[slug];
  const pillar = idx !== undefined ? pillars6[idx] : null;
  const [related, setRelated] = useState([]);

  usePageMeta({
    title: pillar?.label,
    description: pillar
      ? `${pillar.outcome} Focus areas include ${pillar.focusAreas.slice(0, 3).join(", ").toLowerCase()}.`
      : undefined,
  });

  useEffect(() => {
    if (!pillar) return;
    let active = true;
    setRelated([]);
    fetchResources({ related_pillar: pillar.slug })
      .then((r) => active && setRelated(r.slice(0, 3)))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [pillar]);

  if (!pillar) return <Navigate to="/" replace />;
  const others = pillars6.filter((p) => p.slug !== pillar.slug);

  return (
    <main data-testid="service-page" style={{ background: C.surfaceLight, color: C.textOnLight }}>
      <SiteHeader6 />

      {/* Service hero — name, outcome, focus areas box */}
      <section data-testid="service-hero" style={{ background: C.surfaceLight, borderBottom: `1px solid ${C.borderSoft}` }}>
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="mb-12">
            <Breadcrumb trail={[{ label: "Faith 1st Consulting", href: "/" }, { label: "Services", href: "/#services" }, { label: pillar.label }]} />
          </div>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-7">
              <Eyebrow6 className="mb-7">{`Service ${pillar.n} of 04`}</Eyebrow6>
              <h1
                data-testid="interior-title"
                className="font-cormorant"
                style={{ color: C.textOnLight, fontSize: "clamp(2.4rem, 5vw, 4.2rem)", fontWeight: 400, lineHeight: 1.02, letterSpacing: "-0.014em" }}
              >
                {pillar.label}
              </h1>
              <p className="mt-8 font-cormorant" style={{ color: C.surfacePrimary, fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)", lineHeight: 1.3 }} data-testid="service-outcome">
                {pillar.outcome}
              </p>
              <p className="mt-6 font-inter leading-[1.7] max-w-[58ch]" style={{ color: C.inkOnLight, fontSize: "1.05rem" }}>
                {pillar.detailIntro}
              </p>
              <div className="mt-10">
                <Cta6 to="/contact" testId="service-hero-cta" className="px-7 py-4">
                  {brand6.ctaLabel}
                </Cta6>
              </div>
            </div>
            <Box6 as="aside" tone="parchment" radius="card" className="lg:col-span-5 p-8 lg:p-9" data-testid="service-focus-box">
              <FocusAreas6 items={pillar.focusAreas} />
              <p className="mt-6 font-cormorant italic" style={{ color: C.inkOnLightSoft, fontSize: "0.95rem" }}>
                Recognize your situation here? The detail below shows how the work takes shape.
              </p>
            </Box6>
          </div>
        </div>
      </section>

      {/* What this covers */}
      <section data-testid="service-covers" style={{ background: C.surfaceLight2 }}>
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="mb-10">
            <Eyebrow6 className="mb-5">What this covers</Eyebrow6>
            <h2 className="font-cormorant" style={{ color: C.textOnLight, fontSize: "clamp(1.65rem, 2.4vw, 2.2rem)", fontWeight: 400, lineHeight: 1.12, maxWidth: "28ch" }}>
              The work, by area.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5" data-testid="subservices-grid">
            {pillar.subServices.map(([name, body], i) => (
              <Box6 key={name} tone="white" radius="resource" className="p-8" data-testid={`subservice-${i}`}>
                <p
                  className="font-inter text-[10px] uppercase font-semibold pb-3 mb-4"
                  style={{ color: C.accentMetal, letterSpacing: "0.22em", borderBottom: `1px solid ${C.brassSoft}` }}
                >
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="font-cormorant" style={{ color: C.textOnLight, fontSize: "1.3rem", fontWeight: 400, lineHeight: 1.2 }}>
                  {name}
                </h3>
                <p className="mt-3 font-inter leading-[1.65]" style={{ color: C.inkOnLight, fontSize: "0.95rem" }}>
                  {body}
                </p>
              </Box6>
            ))}
          </div>
        </div>
      </section>

      {/* Where it sits / who it's for / shape */}
      <section data-testid="service-shape" style={{ background: C.surfaceLight }}>
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="grid lg:grid-cols-3 gap-5">
            <Box6 tone="blue" radius="resource" className="p-8" data-testid="service-boundary">
              <BoxLabel>Where this sits</BoxLabel>
              <p className="font-inter leading-[1.7]" style={{ color: C.textOnDark, fontSize: "0.98rem" }}>
                {pillar.boundary}
              </p>
            </Box6>
            <Box6 tone="parchment" radius="resource" className="p-8" data-testid="service-who">
              <BoxLabel>Who this is for</BoxLabel>
              <p className="font-inter leading-[1.7]" style={{ color: C.inkOnLight, fontSize: "0.98rem" }}>
                {pillar.whoItsFor}
              </p>
            </Box6>
            <Box6 tone="parchment" radius="resource" className="p-8" data-testid="service-engagement-shape">
              <BoxLabel>How engagements take shape</BoxLabel>
              <ol className="space-y-4">
                {pillar.engagementShape.map((step, i) => (
                  <li key={i} className="flex items-baseline gap-4" data-testid={`shape-step-${i}`}>
                    <span className="font-cormorant tabular-nums shrink-0" style={{ color: C.accentMetal, fontSize: "1.15rem", lineHeight: 1 }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="font-inter leading-[1.6]" style={{ color: C.inkOnLight, fontSize: "0.93rem" }}>
                      {step}
                    </p>
                  </li>
                ))}
              </ol>
            </Box6>
          </div>
        </div>
      </section>

      {/* Related resources */}
      {related.length > 0 && (
        <section data-testid="service-related-resources" style={{ background: C.surfaceLight2 }}>
          <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
            <div className="flex flex-wrap items-baseline justify-between gap-6 mb-8">
              <Eyebrow6>From the Resources library</Eyebrow6>
              <TextLink6 to="/resources">Explore Resources</TextLink6>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {related.map((r, i) => (
                <Box6
                  as={Link}
                  key={r.id}
                  to={`/resources/${r.slug}`}
                  tone="white"
                  radius="resource"
                  className="p-7 transition-colors"
                  data-testid={`related-resource-${i}`}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.surfacePrimary)}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.brassLine)}
                >
                  <h3 className="font-cormorant" style={{ color: C.textOnLight, fontSize: "1.2rem", fontWeight: 400, lineHeight: 1.25 }}>
                    {r.title}
                  </h3>
                  <p className="mt-3 font-inter leading-[1.6]" style={{ color: C.inkOnLight, fontSize: "0.88rem" }}>
                    {r.summary}
                  </p>
                </Box6>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other services */}
      <section data-testid="service-others" style={{ background: C.surfaceLight, borderTop: `1px solid ${C.brassSoft}` }}>
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
          <Eyebrow6 className="mb-8">The other three services</Eyebrow6>
          <div className="grid md:grid-cols-3 gap-5">
            {others.map((p) => (
              <Box6
                as={Link}
                key={p.slug}
                to={`/services/${p.slug}`}
                tone="parchment"
                radius="resource"
                className="group p-7 flex flex-col transition-colors"
                data-testid={`other-service-${p.slug}`}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.surfacePrimary)}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.brassLine)}
              >
                <Pill6 tone="outline" className="self-start">{`Service ${p.n}`}</Pill6>
                <h3 className="mt-4 font-cormorant" style={{ color: C.textOnLight, fontSize: "1.25rem", fontWeight: 400, lineHeight: 1.2 }}>
                  {p.label}
                </h3>
                <p className="mt-3 font-inter leading-[1.6] grow" style={{ color: C.inkOnLight, fontSize: "0.9rem" }}>
                  {p.outcome}
                </p>
                <TextLink6 as="span" className="mt-5">Explore</TextLink6>
              </Box6>
            ))}
          </div>
        </div>
      </section>

      <CtaBand6 testId="service-cta" />
      <SiteFooter6 />
    </main>
  );
}
